import { Button, Flex, Grid, Group, LoadingOverlay, Stack } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { LeavePageNotification } from '@components';
import { useDeclarant, useUpdateDeclarant } from '@hooks/declarant';
import { useSettings } from '@hooks/useSettings';
import { ExcludedList } from './ExcludedList';
import classes from './ManageAccount.module.css';
import { SettingCheckbox } from './SettingCheckbox';

const validationSettingsSchema = z.object({
  validationSettings: z.object({
    h7ExcludedCodesValidationEnabled: z.boolean(),
    IOSSExcludedValidationEnabled: z.boolean(),
    IOSSBlackList: z.array(z.string()),
    VatEoriExcludedValidationEnabled: z.boolean(),
    SameUCRValidationEnabled: z.boolean(),
    VatEoriBlackList: z.array(z.string()),
    YListEnabled: z.boolean(),
    YList: z.array(z.string()),
    b2cThresholdEnabled: z.boolean(),
  }),
});

export const ValidationSettingsForm = () => {
  const { data: settings } = useSettings();
  const { data: declarant, isLoading } = useDeclarant();
  const updateDeclarantMutation = useUpdateDeclarant();

  const buildInitialValues = (dec) => ({
    validationSettings: {
      h7ExcludedCodesValidationEnabled:
        dec.validationSettings?.h7ExcludedCodesValidationEnabled !== undefined ? dec.validationSettings.h7ExcludedCodesValidationEnabled : true,
      IOSSExcludedValidationEnabled: dec.validationSettings?.IOSSExcludedValidationEnabled || false,
      IOSSBlackList: dec.validationSettings?.IOSSBlackList || [],
      VatEoriExcludedValidationEnabled: dec.validationSettings?.VatEoriExcludedValidationEnabled || false,
      SameUCRValidationEnabled: dec.validationSettings?.SameUCRValidationEnabled || false,
      VatEoriBlackList: dec.validationSettings?.VatEoriBlackList || [],
      YListEnabled: dec.validationSettings?.YListEnabled || false,
      YList: dec.validationSettings?.YList || [],
      b2cThresholdEnabled: dec.validationSettings?.b2cThresholdEnabled || false,
    },
  });

  const onSubmit = async (values) => {
    updateDeclarantMutation.mutate({
      declarantId: declarant._id,
      declarant: values,
    });
  };

  const form = useForm({
    initialValues: buildInitialValues(declarant),
    validate: zodResolver(validationSettingsSchema),
  });

  useEffect(() => {
    form.setValues(buildInitialValues(declarant));
    form.resetDirty({} as any);
  }, [declarant]);

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />
      <form onSubmit={form.onSubmit(onSubmit)} className={classes.fillHeight}>
        <Stack gap="md" h="100%">
          <Grid>
            <Grid.Col span={6}>
              <SettingCheckbox
                {...form.getInputProps('validationSettings.h7ExcludedCodesValidationEnabled')}
                label="H7 excluded codes validation enabled"
                checked={form.values.validationSettings.h7ExcludedCodesValidationEnabled}
                description="Disable H7 codes validation when creating new declarations"
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <SettingCheckbox
                {...form.getInputProps('validationSettings.IOSSExcludedValidationEnabled')}
                label="Excluded IOSS validation enabled"
                checked={form.values.validationSettings.IOSSExcludedValidationEnabled}
                description="Disable IOSS validation when creating new declarations"
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <SettingCheckbox
                {...form.getInputProps('validationSettings.VatEoriExcludedValidationEnabled')}
                label="Excluded VAT/EORI validation enabled"
                checked={form.values.validationSettings.VatEoriExcludedValidationEnabled}
                description="Disable VAT/EORI validation when uploading high value batches"
              />
            </Grid.Col>
            {!settings?.isForwarder && (
              <>
                <Grid.Col span={6}>
                  <SettingCheckbox
                    {...form.getInputProps('validationSettings.SameUCRValidationEnabled')}
                    label="Excluded same UCR validation enabled"
                    checked={form.values.validationSettings.SameUCRValidationEnabled}
                    description="Disable same UCR validation when uploading high value batches"
                  />
                </Grid.Col>
              </>
            )}
            <Grid.Col span={6}>
              <SettingCheckbox
                {...form.getInputProps('validationSettings.b2cThresholdEnabled')}
                label={`Excluded B2C High value declaration below ${settings.lvInvoiceTreshold}€ enabled (import only)`}
                checked={form.values.validationSettings.b2cThresholdEnabled}
                description={
                  <>Disable verification that the total value of a declaration is less or equal than {settings.lvInvoiceTreshold}€ for a B2C HV (import only)</>
                }
              />
            </Grid.Col>
          </Grid>
          <Flex gap="md" flex={1} mih={0}>
            {form.values.validationSettings.IOSSExcludedValidationEnabled && (
              <Flex direction="column" w="30%">
                <ExcludedList
                  name="IOSS Code"
                  inputPlaceholder="IOSS Number"
                  list={form.values.validationSettings.IOSSBlackList}
                  onUpdate={(list: string[]) => form.setFieldValue('validationSettings.IOSSBlackList', list)}
                  fill
                />
              </Flex>
            )}
            {form.values.validationSettings.VatEoriExcludedValidationEnabled && (
              <Flex direction="column" w="30%">
                <ExcludedList
                  name="VAT/EORI Code"
                  inputPlaceholder="VAT/EORI Number"
                  list={form.values.validationSettings.VatEoriBlackList}
                  onUpdate={(list: string[]) => form.setFieldValue('validationSettings.VatEoriBlackList', list)}
                  fill
                />
              </Flex>
            )}
          </Flex>
          <Group>
            <Button type="submit" variant="filled" size="lg">
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </>
  );
};
